import { H3, H5 } from '@snsw/react-component-library';
import { FeeLineItems } from 'components/FeeSummary/FeeLineItems';
import { FeeRow } from 'components/FeeSummary/FeeRow';
import { SkeletonFeeRows } from 'components/FeeSummary/SkeletonFeeRows';
import { Box } from 'components/Layout';
import React from 'react';
import styled from 'styled-components';
import type { Fee, FeeComponents } from './types';
import { GelTheme } from 'components/GelTheme';

const Summary = styled.div`
  border: 1px solid #dee3e5;
  border-radius: 4px;
  padding: 1.25rem;
`;
const SubText = styled.div`
  font-size: ${GelTheme.fontSize('xs')};
  margin-top: ${GelTheme.spacing(1)};
`;

export interface FeeSummaryProps {
  isLoading?: boolean;
  title?: string;
  netFeeAmount?: number;
  feeComponents?: FeeComponents;
  subText?: string;
}

const hasQuantityRecursive = (feeComponents?: FeeComponents) =>
  !!feeComponents?.some((f) => (Array.isArray(f) ? hasQuantityRecursive(f) : f.quantity != null));

const calculateTotalRecursive = (feeComponents?: FeeComponents) =>
  (feeComponents as [])?.reduce(
    (total, f) => (Array.isArray(f) ? calculateTotalRecursive(f as Fee[]) + total : ((f as Fee).value ?? 0) + total),
    0
  );

export const FeeSummary = ({ isLoading, netFeeAmount, feeComponents, title, subText }: FeeSummaryProps) => {
  const hasQuantity = hasQuantityRecursive(feeComponents);
  const total = netFeeAmount == null ? calculateTotalRecursive(feeComponents) : netFeeAmount;

  const hasTitle = !!title;
  const showHeading = isLoading || typeof feeComponents != null;

  const hasSubText = subText?.length > 0;
  return (
    <Summary>
      <>
        {showHeading && (
          <>
            {hasTitle && (
              <Box mb={1.5}>
                <H3>{title}</H3>
              </Box>
            )}

            <FeeRow
              isTitleContainer
              value={<H5>Amount</H5>}
              quantity={hasQuantity ? <H5>Quantity</H5> : undefined}
              feeName={<H5>Description</H5>}
            />
          </>
        )}

        {isLoading && <SkeletonFeeRows />}
        {!isLoading && <FeeLineItems values={feeComponents} netFeeAmount={total} />}
        {hasSubText && <SubText>{subText}</SubText>}
      </>
    </Summary>
  );
};

FeeSummary.defaultProps = {
  title: 'Fee summary',
  subText: '*Any applicable merchant fees will be shown on the payment page',
};
